// src/components/Table.jsx

import React from 'react';
import PropTypes from 'prop-types';
import './Table.css';
import ActionButton from '../Common/ActionButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faPencil, faTrash, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useLanguageSettings } from '../Common/LanguageSettingsContext';
import { useColorSettings } from '../Common/ColorSettingsContext';
import Spinner from '../Common/Spinner';

const Table = ({
  data,
  onEdit,
  onDelete,
  onSendMessage,
  onSort,
  sortField,
  sortOrder,
  userRole,
  isMobile,
}) => {
  const { t } = useTranslation();

  const { language, updateLanguage, loading: languageLoading } = useLanguageSettings();
  const { appSettings, loading: colorLoading } = useColorSettings();

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? '▲' : '▼';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate(); // Directly convert to Date object
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const handleSendEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  if (colorLoading || languageLoading) {
    return (
      <div className="table-wrapper">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="table-wrapper">
      <table className="member-table">
        <thead>
          <tr>
            <th onClick={() => onSort('nombre')} scope="col" aria-sort={sortField === 'nombre' ? sortOrder : 'none'}>
              {t('memberTable.fullName')} {renderSortIndicator('nombre')}
            </th>
            <th className="hide-on-mobile" onClick={() => onSort('ciudad')} scope="col" aria-sort={sortField === 'ciudad' ? sortOrder : 'none'}>
              {t('memberTable.city')} {renderSortIndicator('ciudad')}
            </th>
            <th className="hide-on-mobile" onClick={() => onSort('estado')} scope="col" aria-sort={sortField === 'estado' ? sortOrder : 'none'}>
              {t('memberTable.state')} {renderSortIndicator('estado')}
            </th>
            <th className="hide-on-mobile" onClick={() => onSort('fechaNacimiento')} scope="col" aria-sort={sortField === 'fechaNacimiento' ? sortOrder : 'none'}>
              {t('memberTable.birthDate')} {renderSortIndicator('fechaNacimiento')}
            </th>
            <th className="hide-on-mobile" onClick={() => onSort('telefono')} scope="col" aria-sort={sortField === 'telefono' ? sortOrder : 'none'}>
              {t('memberTable.phone')} {renderSortIndicator('telefono')}
            </th>
            <th scope="col">{t('memberTable.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((miembro) => (
              <tr key={miembro.id}>
                <td>{`${miembro.nombre} ${miembro.apellido}`}</td>
                <td className="hide-on-mobile">{miembro.ciudad || 'N/A'}</td>
                <td className="hide-on-mobile">{miembro.estado || 'N/A'}</td>
                <td className="hide-on-mobile">{formatDate(miembro.fechaNacimiento)}</td>
                <td className="hide-on-mobile">{miembro.telefono || 'N/A'}</td>
                <td className="actions-cell">
                  <ActionButton
                    onClick={() => onEdit(miembro)}
                    label={t('memberTable.edit')}
                    icon={"faPencil"}
                    isMobile={isMobile}
                    variant="secondary"
                  />
                  <ActionButton
                    onClick={() => onSendMessage(miembro)}
                    label={t('memberTable.sendMessage')}
                    icon={"faPaperPlane"}
                    isMobile={isMobile}
                    variant="primary"
                  />
                  {isMobile && miembro.telefono && (
                    <ActionButton
                      as="a"
                      href={`tel:${miembro.telefono}`}
                      label={t('memberTable.call')}
                      icon={"faPhone"}
                      isMobile={isMobile}
                      variant="secondary"
                    />
                  )}
                  <ActionButton
                    onClick={miembro.correo ? () => handleSendEmail(miembro.correo) : null}
                    label={t('memberTable.sendEmail')}
                    icon={"faEnvelope"}
                    isMobile={isMobile}
                    variant="info"
                    disabled={!miembro.correo}
                  />
                  {userRole === 'admin' && (
                    <ActionButton
                      onClick={() => onDelete(miembro)}
                      label={t('memberTable.delete')}
                      icon={"faTrash"}
                      isMobile={isMobile}
                      variant="danger"
                    />
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6">{t('memberTable.noRecordsFound')}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

Table.propTypes = {
  data: PropTypes.array.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onSort: PropTypes.func.isRequired,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  userRole: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

export default Table;
